import { useState } from 'react';
import { Button, Dropdown, Icon, IconButton, Tooltip, icon as iconSize } from '@nula/core-ui';
import { detectCodeBlockLanguage, isMermaidBlock } from './code-block-actions';
import styles from './code-block-toolbar.module.css';

export interface CodeBlockLanguage {
  label: string;
  value: string | null;
}

export const CODE_BLOCK_LANGUAGES: CodeBlockLanguage[] = [
  { label: 'TypeScript', value: 'typescript' },
  { label: 'JavaScript', value: 'javascript' },
  { label: 'JSX', value: 'jsx' },
  { label: 'TSX', value: 'tsx' },
  { label: 'Python', value: 'python' },
  { label: 'Rust', value: 'rust' },
  { label: 'Go', value: 'go' },
  { label: 'CSS', value: 'css' },
  { label: 'HTML', value: 'html' },
  { label: 'JSON', value: 'json' },
  { label: 'YAML', value: 'yaml' },
  { label: 'Bash', value: 'bash' },
  { label: 'SQL', value: 'sql' },
  { label: 'Markdown', value: 'markdown' },
  { label: 'Mermaid', value: 'mermaid' },
  { label: 'C', value: 'c' },
  { label: 'C++', value: 'cpp' },
  { label: 'Java', value: 'java' },
  { label: 'Swift', value: 'swift' },
  { label: 'Kotlin', value: 'kotlin' },
  { label: 'Ruby', value: 'ruby' },
  { label: 'PHP', value: 'php' },
];

export interface CodeBlockToolbarProps {
  language: string | null;
  getText: () => string;
  languages?: CodeBlockLanguage[];
  onCopy: () => void;
  onDelete: () => void;
  onLanguageChange: (lang: string | null) => void;
  /** Toggle the mermaid diagram/code view. When the block is mermaid, the
   *  toolbar shows a single neutral toggle next to copy/delete. */
  onToggleView?: () => void;
}

/** Inline toolbar — rendered inside the code block node, scrolls with it naturally. */
export function CodeBlockToolbar({
  language,
  languages = CODE_BLOCK_LANGUAGES,
  getText,
  onCopy,
  onDelete,
  onLanguageChange,
  onToggleView,
}: CodeBlockToolbarProps) {
  const [copied, setCopied] = useState(false);
  const isMermaid = isMermaidBlock(language, getText());
  const showDiagramToggle = isMermaid && !!onToggleView;

  // Resolve the label shown in the trigger:
  //  - mermaid (explicit OR content-detected) → "Mermaid".
  //  - explicit language set → its dropdown label (or the raw value).
  //  - no language → show what highlight.js auto-detected, suffixed "(auto)",
  //    so the user sees what the block is being highlighted as. There is no
  //    "Plain Text" option — lowlight always auto-detects when unset.
  const labelFor = (value: string | null) =>
    languages.find((l) => l.value === value)?.label ?? value ?? 'Language';
  const detected = language ? null : detectCodeBlockLanguage(getText());
  const currentLabel = isMermaid
    ? 'Mermaid'
    : language
      ? labelFor(language)
      : `${labelFor(detected)} (auto)`;

  const handleCopy = () => {
    onCopy();
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div
      className={styles.toolbar}
      contentEditable={false}
      onMouseDown={(e) => e.preventDefault()}
    >
      <Dropdown
        trigger={['click']}
        menuStyle={{ maxHeight: '240px', overflowY: 'auto' }}
        menu={{
          items: languages.map((l) => ({
            key: l.value ?? '__plain__',
            label: l.label,
            onClick: () => onLanguageChange(l.value),
          })),
        }}
      >
        <Button size="sm" variant="text" className={styles.langButton} htmlType="button">
          {currentLabel}
          <Icon name="arrow_drop_down" size={iconSize.sm} weight={300} />
        </Button>
      </Dropdown>

      {showDiagramToggle && (
        <Tooltip title="Toggle diagram / code" placement="top" mouseEnterDelay={600}>
          <IconButton
            size="sm"
            transparent
            pill
            aria-label="Toggle diagram view"
            onClick={onToggleView}
          >
            <Icon name="swap_horiz" size={iconSize.sm} weight={300} />
          </IconButton>
        </Tooltip>
      )}

      <Tooltip title={copied ? 'Copied!' : 'Copy'} placement="top" mouseEnterDelay={600}>
        <IconButton size="sm" transparent pill aria-label="Copy code" onClick={handleCopy}>
          <Icon name={copied ? 'check' : 'content_copy'} size={iconSize.sm} weight={300} />
        </IconButton>
      </Tooltip>

      <Tooltip title="Delete" placement="top" mouseEnterDelay={600}>
        <IconButton size="sm" transparent pill aria-label="Delete block" onClick={onDelete}>
          <Icon name="delete" size={iconSize.sm} weight={300} />
        </IconButton>
      </Tooltip>
    </div>
  );
}
